import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  alpha,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  ShoppingCart as OrderIcon,
  Store as SupplierIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  PieChart as PieChartIcon,
  Event as EventIcon,
  Cake as CakeIcon,
  Favorite as AnniversaryIcon,
  Send as SendIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { DataContext } from "../../context/DataContext";
import { format, parse, isValid } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useNavigate } from "react-router-dom";

// Update the date parsing in calculateUpcomingEvents function:
const calculateUpcomingEvents = (customers, daysAhead = 7) => {
  if (!customers || !Array.isArray(customers)) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const futureDate = new Date();
  futureDate.setDate(today.getDate() + daysAhead);
  futureDate.setHours(23, 59, 59, 999);

  const events = [];

  // Helper function to safely get string value
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (value instanceof Date) return format(value, 'dd/MM/yyyy');
    if (typeof value === 'number') return String(value);
    return String(value);
  };

  // Helper function to safely trim a string
  const safeTrim = (value) => {
    const str = safeString(value);
    return str.trim();
  };

  // Helper function to safely parse date
  const safeParseDate = (dateStr) => {
    if (!dateStr) return null;

    try {
      const cleanStr = safeTrim(dateStr);
      if (!cleanStr) return null;

      // Try parsing with dd/MM/yyyy format
      const parsed = parse(cleanStr, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) return parsed;

      // Try other common formats
      const formats = ["dd-MM-yyyy", "dd.MM.yyyy", "yyyy/MM/dd", "yyyy-MM-dd"];
      for (const fmt of formats) {
        const altParsed = parse(cleanStr, fmt, new Date());
        if (isValid(altParsed)) return altParsed;
      }

      // Try native Date parsing as fallback
      const nativeDate = new Date(cleanStr);
      if (isValid(nativeDate)) return nativeDate;

      return null;
    } catch (error) {
      console.warn("Error parsing date:", dateStr, error);
      return null;
    }
  };

  // Helper function to check if date is in range
  const isDateInRange = (date) => {
    if (!date || !isValid(date)) return false;
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    return dateCopy >= today && dateCopy <= futureDate;
  };

  customers.forEach(customer => {
    if (!customer) return;

    // Check customer's own DOB
    const customerDob = safeString(customer.dob);
    if (customerDob) {
      const dobDate = safeParseDate(customerDob);
      if (dobDate && isValid(dobDate)) {
        // Create date for this year
        const currentYearDob = new Date(today.getFullYear(), dobDate.getMonth(), dobDate.getDate());
        const nextYearDob = new Date(today.getFullYear() + 1, dobDate.getMonth(), dobDate.getDate());

        let upcomingDob = null;
        if (isDateInRange(currentYearDob)) {
          upcomingDob = currentYearDob;
        } else if (isDateInRange(nextYearDob)) {
          upcomingDob = nextYearDob;
        }

        if (upcomingDob) {
          events.push({
            id: `${customer.id}-dob`,
            name: safeString(customer.name) || 'Unknown Customer',
            type: "Birthday",
            date: upcomingDob,
            dateFormatted: format(upcomingDob, 'dd MMM yyyy'),
            relation: "Primary Customer",
            customerId: customer.id,
            contact: safeString(customer.contact),
            email: safeString(customer.emailId),
            isPrimary: true
          });
        }
      }
    }

    // Check customer's own anniversary
    const customerAnniversary = safeString(customer.anniversary);
    if (customerAnniversary) {
      const annivDate = safeParseDate(customerAnniversary);
      if (annivDate && isValid(annivDate)) {
        const currentYearAnniv = new Date(today.getFullYear(), annivDate.getMonth(), annivDate.getDate());
        const nextYearAnniv = new Date(today.getFullYear() + 1, annivDate.getMonth(), annivDate.getDate());

        let upcomingAnniv = null;
        if (isDateInRange(currentYearAnniv)) {
          upcomingAnniv = currentYearAnniv;
        } else if (isDateInRange(nextYearAnniv)) {
          upcomingAnniv = nextYearAnniv;
        }

        if (upcomingAnniv) {
          events.push({
            id: `${customer.id}-anniv`,
            name: safeString(customer.name) || 'Unknown Customer',
            type: "Anniversary",
            date: upcomingAnniv,
            dateFormatted: format(upcomingAnniv, 'dd MMM yyyy'),
            relation: "Primary Customer",
            customerId: customer.id,
            contact: safeString(customer.contact),
            email: safeString(customer.emailId),
            isPrimary: true
          });
        }
      }
    }

    // Check family members (if the field exists and is an array)
    if (customer.familyMembers && Array.isArray(customer.familyMembers)) {
      customer.familyMembers.forEach((member, index) => {
        if (!member) return;

        const memberName = safeString(member.name);
        if (!memberName) return;

        // Check member's DOB
        const memberDob = safeString(member.dob);
        if (memberDob) {
          const memberDobDate = safeParseDate(memberDob);
          if (memberDobDate && isValid(memberDobDate)) {
            const currentYearMemberDob = new Date(today.getFullYear(), memberDobDate.getMonth(), memberDobDate.getDate());
            const nextYearMemberDob = new Date(today.getFullYear() + 1, memberDobDate.getMonth(), memberDobDate.getDate());

            let upcomingMemberDob = null;
            if (isDateInRange(currentYearMemberDob)) {
              upcomingMemberDob = currentYearMemberDob;
            } else if (isDateInRange(nextYearMemberDob)) {
              upcomingMemberDob = nextYearMemberDob;
            }

            if (upcomingMemberDob) {
              events.push({
                id: `${customer.id}-family-${index}-dob`,
                name: memberName,
                type: "Birthday",
                date: upcomingMemberDob,
                dateFormatted: format(upcomingMemberDob, 'dd MMM yyyy'),
                relation: safeString(member.relationship) || "Family Member",
                customerId: customer.id,
                contact: safeString(member.contact) || safeString(customer.contact),
                email: safeString(customer.emailId),
                isPrimary: false,
                mainCustomer: safeString(customer.name) || 'Unknown Customer'
              });
            }
          }
        }

        // Check member's anniversary
        const memberAnniversary = safeString(member.anniversary);
        if (memberAnniversary) {
          const memberAnnivDate = safeParseDate(memberAnniversary);
          if (memberAnnivDate && isValid(memberAnnivDate)) {
            const currentYearMemberAnniv = new Date(today.getFullYear(), memberAnnivDate.getMonth(), memberAnnivDate.getDate());
            const nextYearMemberAnniv = new Date(today.getFullYear() + 1, memberAnnivDate.getMonth(), memberAnnivDate.getDate());

            let upcomingMemberAnniv = null;
            if (isDateInRange(currentYearMemberAnniv)) {
              upcomingMemberAnniv = currentYearMemberAnniv;
            } else if (isDateInRange(nextYearMemberAnniv)) {
              upcomingMemberAnniv = nextYearMemberAnniv;
            }

            if (upcomingMemberAnniv) {
              events.push({
                id: `${customer.id}-family-${index}-anniv`,
                name: memberName,
                type: "Anniversary",
                date: upcomingMemberAnniv,
                dateFormatted: format(upcomingMemberAnniv, 'dd MMM yyyy'),
                relation: safeString(member.relationship) || "Family Member",
                customerId: customer.id,
                contact: safeString(member.contact) || safeString(customer.contact),
                email: safeString(customer.emailId),
                isPrimary: false,
                mainCustomer: safeString(customer.name) || 'Unknown Customer'
              });
            }
          }
        }
      });
    }
  });

  // Sort events by date (closest first)
  return events.sort((a, b) => a.date - b.date);
};


// Wish Sending Modal Component (unchanged)
const WishSendingModal = ({ open, onClose, event, isDark }) => {
  const [message, setMessage] = useState("");
  const [method, setMethod] = useState("whatsapp");
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);


  useEffect(() => {
    if (event) {
      const defaultMessages = {
        Birthday: `Happy Birthday ${event?.name}! 🎉 Wishing you a wonderful day filled with joy and happiness! From ${localStorage.getItem('businessName') || 'JewelStock Pro'}`,
        Anniversary: `Happy Anniversary ${event?.name}! 💕 Wishing you both endless love and happiness! From ${localStorage.getItem('businessName') || 'JewelStock Pro'}`
      };
      setMessage(defaultMessages[event.type]);
    }
  }, [event]);

  const handleSendWish = async () => {
    if (!event || !message.trim()) return;

    setIsSending(true);

    // Simulate API call
    setTimeout(() => {
      setIsSending(false);
      setSendSuccess(true);

      // Simulate sending based on method
      console.log(`Sending ${event.type} wish via ${method}:`, {
        to: event.name,
        contact: event.contact,
        email: event.email,
        message
      });

      // Reset after 2 seconds
      setTimeout(() => {
        setSendSuccess(false);
        onClose();
      }, 2000);
    }, 1500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '16px', sm: '20px' },
          margin: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          background: isDark
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(30px)',
          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.4)'
            : '0 20px 60px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{
        p: { xs: 2, sm: 3 },
        pb: { xs: 1.5, sm: 2 },
        background: isDark
          ? 'linear-gradient(90deg, rgba(236, 72, 153, 0.1) 0%, rgba(219, 39, 119, 0.05) 100%)'
          : 'linear-gradient(90deg, rgba(236, 72, 153, 0.08) 0%, rgba(219, 39, 119, 0.04) 100%)',
        borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: { xs: '10px', sm: '12px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark
              ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(219, 39, 119, 0.2) 100%)'
              : 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(219, 39, 119, 0.15) 100%)',
            border: `1.5px solid ${alpha('#ec4899', isDark ? 0.3 : 0.2)}`,
          }}>
            {event?.type === 'Birthday' ? (
              <CakeIcon sx={{ color: '#ec4899', fontSize: { xs: 20, sm: 24 } }} />
            ) : (
              <AnniversaryIcon sx={{ color: '#ec4899', fontSize: { xs: 20, sm: 24 } }} />
            )}
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              color: isDark ? '#f1f5f9' : '#1e293b',
            }}>
              Send {event?.type} Wishes
            </Typography>
            <Typography variant="body2" sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              mt: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }}>
              {event?.name} • {event?.dateFormatted}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {sendSuccess ? (
          <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
            <Box sx={{
              width: { xs: 60, sm: 80 },
              height: { xs: 60, sm: 80 },
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isDark
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
              border: `2px solid ${alpha('#10b981', isDark ? 0.3 : 0.2)}`,
              mx: 'auto',
              mb: 3,
            }}>
              <SendIcon sx={{ color: '#10b981', fontSize: { xs: 24, sm: 32 } }} />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              color: isDark ? '#f1f5f9' : '#1e293b',
              mb: 1,
            }}>
              Wish Sent Successfully!
            </Typography>
            <Typography variant="body2" sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              lineHeight: 1.6,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}>
              Your {event?.type.toLowerCase()} wishes have been sent to {event?.name}.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                mb: 2,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}>
                Send personalized {event?.type.toLowerCase()} wishes to your customer
              </Typography>

              {/* Sending Method */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel sx={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  '&.Mui-focused': {
                    color: isDark ? '#ec4899' : '#ec4899',
                  }
                }}>
                  Send Via
                </InputLabel>
                <Select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  label="Send Via"
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                    border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                    '& .MuiSelect-select': {
                      color: isDark ? '#f1f5f9' : '#1e293b',
                      padding: { xs: '8px 12px', sm: '12px 16px' },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    },
                  }}
                >
                  <MenuItem value="whatsapp">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsAppIcon sx={{ color: '#25D366', fontSize: { xs: 18, sm: 20 } }} />
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>WhatsApp</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="sms">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmsIcon sx={{ color: isDark ? '#60a5fa' : '#2563eb', fontSize: { xs: 18, sm: 20 } }} />
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>SMS</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="email">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ color: isDark ? '#f87171' : '#ef4444', fontSize: { xs: 18, sm: 20 } }} />
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Email</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Message Input */}
              <TextField
                fullWidth
                multiline
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isDark ? alpha('#1e293b', 0.5) : alpha('#ffffff', 0.9),
                    border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                    '&:hover': {
                      borderColor: alpha('#ec4899', isDark ? 0.5 : 0.4),
                    },
                    '&.Mui-focused': {
                      borderColor: '#ec4899',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
              />
            </Box>

            {/* Contact Info */}
            <Box sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: isDark ? alpha('#1e293b', 0.3) : alpha('#f8fafc', 0.8),
              border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
              mb: 3,
            }}>
              <Typography variant="caption" sx={{
                fontWeight: 600,
                color: isDark ? '#cbd5e1' : '#475569',
                mb: 1,
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}>
                Sending to:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {event?.contact && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ color: '#25D366', fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {event.contact}
                    </Typography>
                  </Box>
                )}
                {event?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon sx={{ color: '#ef4444', fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {event.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: { xs: 2, sm: 3 },
        pt: 0,
        justifyContent: 'space-between',
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: isDark ? '#94a3b8' : '#64748b',
            borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.5),
            fontWeight: 500,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            '&:hover': {
              borderColor: alpha(isDark ? '#94a3b8' : '#64748b', 0.8),
            },
          }}
        >
          Cancel
        </Button>

        {!sendSuccess && (
          <Button
            onClick={handleSendWish}
            variant="contained"
            disabled={isSending || !message.trim()}
            sx={{
              borderRadius: '12px',
              fontWeight: 600,
              px: { xs: 2, sm: 4 },
              py: { xs: 0.8, sm: 1 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
              boxShadow: '0 8px 25px rgba(236, 72, 153, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(236, 72, 153, 0.4)',
              },
              '&.Mui-disabled': {
                background: alpha('#EC4899', 0.3),
                color: alpha('#fff', 0.5),
              },
            }}
          >
            {isSending ? (
              <>
                <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                Sending...
              </>
            ) : (
              <>
                <SendIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1 }} />
                Send Wish
              </>
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};


// Enhanced EventsReminderCard Component
const EventsReminderCard = ({ customers, isDark }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [wishModalOpen, setWishModalOpen] = useState(false);
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('today');

  const upcomingEvents = useMemo(() =>
    calculateUpcomingEvents(customers, 30),
    [customers]
  );

  const todayEvents = useMemo(() => {
    const today = new Date();
    return upcomingEvents.filter(event =>
      format(event.date, 'dd/MM/yyyy') === format(today, 'dd/MM/yyyy')
    );
  }, [upcomingEvents]);

  const displayedEvents = viewMode === 'today' ? todayEvents : upcomingEvents.slice(0, 2);

  // const handleSendWish = (event) => {
  //   setSelectedEvent(event);
  //   setWishModalOpen(true);
  // };

  const getEventIcon = (type) => {
    return type === 'Birthday' ?
      <CakeIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> :
      <AnniversaryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
  };

  const getEventColor = (type) => {
    return type === 'Birthday' ? '#3B82F6' : '#EC4899';
  };

  return (
    <>
      <Card
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: isMobile ? 'auto' : '340px',
          position: "relative",
          overflow: "hidden",
          backgroundColor: isDark
            ? "rgba(30, 41, 59, 0.4)"
            : "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(255, 255, 255, 0.4)"}`,
          borderRadius: { xs: "16px", sm: "20px" },
          boxShadow: isDark
            ? "0 4px 24px rgba(0, 0, 0, 0.4)"
            : "0 4px 24px rgba(0, 0, 0, 0.06)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            p: { xs: 2, sm: 2.5 },
            pb: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  p: 1.2,
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #F97316 0%, #EC4899 100%)",
                  boxShadow: "0 8px 22px rgba(248, 113, 113, 0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EventIcon sx={{ color: "#fff", fontSize: { xs: 18, sm: 22 } }} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                    color: isDark ? "#f1f5f9" : "#1e293b",
                  }}
                >
                  Upcoming Celebrations
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  }}
                >
                  Birthdays & Anniversaries
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={`${todayEvents.length} today`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  borderRadius: "999px",
                  backgroundColor: alpha("#22C55E", 0.08),
                  border: `1px solid ${alpha("#22C55E", 0.3)}`,
                  color: "#16A34A",
                }}
                onClick={() => setViewMode('today')}
                clickable
              />
              <Chip
                label={`${upcomingEvents.length} total`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  borderRadius: "999px",
                  backgroundColor: alpha("#3B82F6", 0.08),
                  border: `1px solid ${alpha("#3B82F6", 0.3)}`,
                  color: "#1D4ED8",
                }}
                onClick={() => setViewMode('upcoming')}
                clickable
              />
            </Box>
          </Box>

          {/* View Mode Toggle */}
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant={viewMode === 'today' ? "contained" : "outlined"}
              onClick={() => setViewMode('today')}
              sx={{
                borderRadius: "10px",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 600,
                textTransform: "none",
                px: { xs: 1.5, sm: 2 },
                py: 0.5,
                background: viewMode === 'today'
                  ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                  : "transparent",
                borderColor: viewMode === 'today' ? "transparent" : "#cbd5e1",
                color: viewMode === 'today' ? "white" : isDark ? "#94a3b8" : "#64748b",
              }}
            >
              Today
            </Button>
            <Button
              size="small"
              variant={viewMode === 'upcoming' ? "contained" : "outlined"}
              onClick={() => setViewMode('upcoming')}
              sx={{
                borderRadius: "10px",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 600,
                textTransform: "none",
                px: { xs: 1.5, sm: 2 },
                py: 0.5,
                background: viewMode === 'upcoming'
                  ? "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)"
                  : "transparent",
                borderColor: viewMode === 'upcoming' ? "transparent" : "#cbd5e1",
                color: viewMode === 'upcoming' ? "white" : isDark ? "#94a3b8" : "#64748b",
              }}
            >
              Upcoming
            </Button>
          </Box>

          {/* Events List */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              flex: 1,
              maxHeight: isMobile ? "auto" : "300px",
              overflowY: isMobile ? "visible" : "auto",
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: isDark ? alpha('#1e293b', 0.5) : alpha('#f1f5f9', 0.5),
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? '#475569' : '#cbd5e1',
                borderRadius: '10px',
                '&:hover': {
                  background: isDark ? '#64748b' : '#94a3b8',
                }
              }
            }}
          >
            {displayedEvents.length > 0 ? (
              displayedEvents.map((event) => (
                <Box
                  key={event.id}
                  sx={{
                    p: { xs: 1, sm: 1.3 },
                    borderRadius: "14px",
                    backgroundColor: isDark
                      ? "rgba(15, 23, 42, 0.7)"
                      : "rgba(255, 255, 255, 0.9)",
                    border: `1px solid ${alpha(getEventColor(event.type), 0.2)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(30, 41, 59, 0.9)"
                        : "rgba(255, 255, 255, 1)",
                      borderColor: alpha(getEventColor(event.type), 0.4),
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1 }}>
                    <Avatar
                      sx={{
                        width: { xs: 32, sm: 36 },
                        height: { xs: 32, sm: 36 },
                        bgcolor: alpha(getEventColor(event.type), 0.1),
                        color: getEventColor(event.type),
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    >
                      {getEventIcon(event.type)}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          color: isDark ? "#e2e8f0" : "#1e293b",
                        }}
                      >
                        {event.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "#94a3b8" : "#64748b",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        }}
                      >
                        {event.type} • {event.relation}
                        {!event.isPrimary && ` (${event.mainCustomer})`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        color: isDark ? "#FBBF24" : "#D97706",
                        fontFamily: "monospace",
                      }}
                    >
                      {event.dateFormatted}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  p: 3,
                  borderRadius: "14px",
                  backgroundColor: isDark
                    ? "rgba(15, 23, 42, 0.5)"
                    : "rgba(248, 250, 252, 0.8)",
                  border: `1px dashed ${alpha(isDark ? "#475569" : "#cbd5e1", 0.4)}`,
                  textAlign: "center",
                }}
              >
                <EventIcon
                  sx={{
                    fontSize: { xs: 28, sm: 32 },
                    color: isDark ? "#475569" : "#94a3b8",
                    mb: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    mb: 0.5,
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  }}
                >
                  No upcoming events
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? "#64748b" : "#94a3b8",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                >
                  {viewMode === 'today'
                    ? 'No birthdays or anniversaries today'
                    : 'No birthdays or anniversaries in the next 30 days'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              pt: 1,
              mt: 1,
              borderTop: `1px solid ${alpha(isDark ? "#334155" : "#e2e8f0", 0.4)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isDark ? "#94a3b8" : "#64748b",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            >
              {viewMode === 'today'
                ? `${todayEvents.length} events today`
                : `${upcomingEvents.length} events in next 30 days`}
            </Typography>

            {viewMode === 'upcoming' && upcomingEvents.length > 5 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setViewAllModalOpen(true)}
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  fontWeight: 600,
                  textTransform: "none",
                  color: isDark ? "#60a5fa" : "#2563eb",
                  "&:hover": {
                    backgroundColor: alpha(isDark ? "#60a5fa" : "#2563eb", 0.1),
                  },
                }}
              >
                +{upcomingEvents.length - 2} more
              </Button>
            )}

            {upcomingEvents.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setViewAllModalOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  px: { xs: 1, sm: 1.5 },
                  py: 0.3,
                  borderColor: alpha("#6366F1", 0.5),
                  color: isDark ? "#A5B4FC" : "#4F46E5",
                  "&:hover": {
                    borderColor: "#6366F1",
                    backgroundColor: alpha("#6366F1", 0.04),
                  },
                }}
              >
                View all
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <WishSendingModal
        open={wishModalOpen}
        onClose={() => setWishModalOpen(false)}
        event={selectedEvent}
        isDark={isDark}
      />

      <ViewAllEventsModal
        open={viewAllModalOpen}
        onClose={() => setViewAllModalOpen(false)}
        events={viewMode === 'today' ? todayEvents : upcomingEvents}
        viewMode={viewMode}
        isDark={isDark}
        getEventColor={getEventColor}
        getEventIcon={getEventIcon}
        setSelectedEvent={setSelectedEvent}
        setWishModalOpen={setWishModalOpen}
        setViewAllModalOpen={setViewAllModalOpen}
      />
    </>
  );
};

// Separate ViewAllEventsModal Component
const ViewAllEventsModal = ({
  open,
  onClose,
  events,
  viewMode,
  isDark,
  getEventColor,
  getEventIcon,
  setSelectedEvent,
  setWishModalOpen,
  setViewAllModalOpen
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '16px', sm: '20px' },
          margin: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          background: isDark
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(30px)',
          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.4)'
            : '0 20px 60px rgba(0, 0, 0, 0.1)',
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{
        p: { xs: 2, sm: 3 },
        pb: { xs: 1.5, sm: 2 },
        background: isDark
          ? 'linear-gradient(90deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)'
          : 'linear-gradient(90deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%)',
        borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: { xs: '10px', sm: '12px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark
              ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)'
              : 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
            border: `1.5px solid ${alpha('#F59E0B', isDark ? 0.3 : 0.2)}`,
          }}>
            <EventIcon sx={{ color: '#F59E0B', fontSize: { xs: 20, sm: 24 } }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              color: isDark ? '#f1f5f9' : '#1e293b',
            }}>
              {viewMode === 'today' ? "Today's Celebrations" : "Upcoming Celebrations"}
            </Typography>
            <Typography variant="body2" sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              mt: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }}>
              {events.length} events • Birthdays & Anniversaries
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          maxHeight: isMobile ? 'auto' : '400px',
          overflowY: isMobile ? 'visible' : 'auto',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: isDark ? alpha('#1e293b', 0.5) : alpha('#f1f5f9', 0.5),
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: isDark ? '#475569' : '#cbd5e1',
            borderRadius: '10px',
            '&:hover': {
              background: isDark ? '#64748b' : '#94a3b8',
            }
          }
        }}>
          {events.length > 0 ? (
            events.map((event) => (
              <Card
                key={event.id}
                sx={{
                  borderRadius: '12px',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  border: `1.5px solid ${alpha(getEventColor(event.type), 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(getEventColor(event.type), 0.4),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <Avatar
                        sx={{
                          width: { xs: 36, sm: 42 },
                          height: { xs: 36, sm: 42 },
                          bgcolor: alpha(getEventColor(event.type), 0.1),
                          color: getEventColor(event.type),
                          fontWeight: 600,
                        }}
                      >
                        {getEventIcon(event.type)}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          }}
                        >
                          {event.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? '#94a3b8' : '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'wrap',
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          }}
                        >
                          {event.type} • {event.relation}
                          {!event.isPrimary && ` (${event.mainCustomer})`}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'right', minWidth: { xs: '100px', sm: '120px' } }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          color: isDark ? '#FBBF24' : '#D97706',
                          fontFamily: 'monospace',
                          mb: 0.5,
                        }}
                      >
                        {event.dateFormatted}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Box sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: '12px',
              backgroundColor: isDark
                ? alpha('#1e293b', 0.5)
                : alpha('#f8fafc', 0.8),
              border: `1px dashed ${alpha(isDark ? '#475569' : '#cbd5e1', 0.4)}`,
            }}>
              <EventIcon sx={{
                fontSize: { xs: 36, sm: 48 },
                color: isDark ? '#475569' : '#94a3b8',
                mb: 2,
                opacity: 0.5
              }} />
              <Typography variant="h6" sx={{
                color: isDark ? '#f1f5f9' : '#1e293b',
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}>
                No Events Found
              </Typography>
              <Typography variant="body2" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}>
                {viewMode === 'today'
                  ? 'No birthdays or anniversaries today'
                  : 'No upcoming events in the next 30 days'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: { xs: 2, sm: 3 },
        pt: { xs: 1.5, sm: 2 },
        borderTop: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '12px',
            fontWeight: 600,
            textTransform: 'none',
            px: { xs: 2, sm: 4 },
            py: 1,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.5),
            color: isDark ? '#cbd5e1' : '#475569',
            '&:hover': {
              borderColor: isDark ? '#64748b' : '#94a3b8',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// UPDATED: Vertical Modern Glassmorphism Stat Card
const GlassStatCard = ({
  icon: Icon,
  title,
  value,
  color,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        width: "100%",
        height: { xs: "140px", sm: "160px" },
        position: "relative",
        overflow: "hidden",
        backgroundColor: isDark
          ? "rgba(30, 41, 59, 0.4)"
          : "rgba(255, 255, 255, 0.6)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
        borderRadius: { xs: "16px", sm: "20px" },
        boxShadow: isDark
          ? "0 8px 32px rgba(0, 0, 0, 0.2)"
          : "0 8px 32px rgba(37, 99, 235, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isDark
            ? "0 12px 40px rgba(0, 0, 0, 0.25)"
            : "0 12px 40px rgba(37, 99, 235, 0.12)",
          backgroundColor: isDark
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.7)",
          "& .gradient-bg": {
            opacity: 0.2,
          }
        },
      }}
    >
      {/* Gradient Background Effect */}
      <Box
        className="gradient-bg"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: `linear-gradient(135deg, ${color}15 0%, transparent 70%)`,
          opacity: 0.1,
          transition: "opacity 0.3s ease",
        }}
      />

      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Top Section: Title */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(30, 41, 59, 0.6)",
              display: "block",
              mb: 1,
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Middle Section: Value */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.2rem", sm: "1.6rem" },
              color: "text.primary",
              lineHeight: 1,
              background: isDark
                ? `linear-gradient(135deg, ${color} 0%, ${theme.palette.primary.light} 100%)`
                : `linear-gradient(135deg, ${color} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {value}
          </Typography>
        </Box>

        {/* Bottom Section: Status Indicator */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "auto",
          }}
        >
          {/* Status Indicator */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: { xs: 8, sm: 10 },
                height: { xs: 8, sm: 10 },
                borderRadius: "50%",
                backgroundColor: color,
                opacity: 0.8,
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 0.8 },
                  "50%": { opacity: 0.4 },
                  "100%": { opacity: 0.8 },
                }
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                fontWeight: 500,
                color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(30, 41, 59, 0.5)",
              }}
            >
              Live
            </Typography>
          </Box>

          {/* Decorative Line */}
          <Box
            sx={{
              width: { xs: 16, sm: 24 },
              height: 3,
              background: `linear-gradient(90deg, ${color} 0%, transparent 100%)`,
              borderRadius: 2,
              opacity: 0.7,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// UPDATED: Calculate dashboard statistics from real data
const calculateDashboardStats = (customers, stock, customerTransactions) => {
  console.log("Calculating real dashboard stats...");

  // Calculate total customers
  const totalCustomers = customers?.length || 0;

  // Calculate total revenue from sold stock items
  let totalRevenue = 0;
  let soldItemsCount = 0;
  let totalInventoryValue = 0;

  stock?.forEach(item => {
    const weight = parseFloat(item.weight) || 0;
    const buyingRate = parseFloat(item.buyingRate) || parseFloat(item.goldCuttingRate) || 0;
    const itemValue = weight * buyingRate;
    totalInventoryValue += itemValue;

    if (item.sold === "Yes") {
      soldItemsCount++;
      totalRevenue += itemValue;
    }
  });

  // Calculate total orders (customer transactions)
  const totalOrders = customerTransactions?.length || 0;


  // Calculate total pending amount from customer transactions
  let totalPendingAmount = 0;
  customerTransactions?.forEach(transaction => {
    const pending = parseFloat(transaction.summary?.pendingAmount) || 0;
    totalPendingAmount += pending;
  });

  console.log("Dashboard stats calculated:", {
    totalCustomers,
    totalRevenue,
    totalOrders,
    totalPendingAmount,
    soldItemsCount,
    totalInventoryValue
  });

  return [
    {
      title: "Total Customers",
      value: totalCustomers.toLocaleString(),
      icon: PeopleIcon,
      color: "#10B981",
      positive: true,
    },
    {
      title: "Total Revenue",
      value: `₹${(totalRevenue / 100000).toFixed(1)}L`,
      icon: TrendingUpIcon,
      color: "#8B5CF6",
      positive: true,
    },
    {
      title: "Total Orders",
      value: totalOrders.toLocaleString(),
      icon: OrderIcon,
      color: "#F59E0B",
      positive: false,
    },
    {
      title: "Pending Amount",
      value: `₹${(totalPendingAmount / 1000).toFixed(1)}K`,
      icon: MoneyIcon,
      color: "#EF4444",
      positive: false,
    }
  ];
};

// UPDATED: Supplier Sales Percentage Card with View All modal
const SupplierSalesCard = ({ stock }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);

  // Calculate supplier sales percentage from real stock data
  const supplierSalesData = useMemo(() => {
    if (!stock || !Array.isArray(stock) || stock.length === 0) {
      console.log("No stock data available");
      return [];
    }

    // Create a map to aggregate supplier data
    const supplierMap = {};

    stock.forEach(item => {
      const supplierName = item.supplierName?.trim();

      // Skip items without supplier name
      if (!supplierName) return;

      // Normalize supplier name (case insensitive)
      const normalizedSupplier = supplierName.toLowerCase();

      // Initialize supplier data if not exists
      if (!supplierMap[normalizedSupplier]) {
        supplierMap[normalizedSupplier] = {
          supplier: supplierName,
          totalSales: 0,
          soldItems: 0,
          totalItems: 0,
          totalWeight: 0,
          totalValue: 0,
        };
      }

      // Update supplier statistics
      const supplier = supplierMap[normalizedSupplier];
      supplier.totalItems++;

      const weight = parseFloat(item.weight) || 0;
      const buyingRate = parseFloat(item.buyingRate) || parseFloat(item.goldCuttingRate) || 0;
      const itemValue = weight * buyingRate;

      supplier.totalWeight += weight;
      supplier.totalValue += itemValue;

      // If item is sold, add to sales
      if (item.sold === "Yes") {
        supplier.soldItems++;
        supplier.totalSales += itemValue;
      }
    });

    // Convert map to array and sort by total sales
    const allSuppliers = Object.values(supplierMap)
      .filter(supplier => supplier.totalSales > 0) // Only include suppliers with sales
      .sort((a, b) => b.totalSales - a.totalSales);

    // Calculate percentages for all suppliers
    const totalSalesAllSuppliers = allSuppliers.reduce(
      (sum, supplier) => sum + supplier.totalSales,
      0
    );

    // Assign colors
    const colors = ["#F59E0B", "#94A3B8", "#8B5CF6", "#3B82F6", "#EF4444", "#10B981", "#EC4899", "#8B5CF6", "#3B82F6", "#F59E0B"];

    return allSuppliers.map((supplier, index) => {
      const percentage = totalSalesAllSuppliers > 0
        ? (supplier.totalSales / totalSalesAllSuppliers) * 100
        : 0;

      // Calculate growth (simulated - in real app, you'd compare with previous period)
      const growth = Math.random() > 0.3
        ? `+${(Math.random() * 15 + 5).toFixed(1)}%`
        : `-${(Math.random() * 5 + 2).toFixed(1)}%`;

      return {
        id: index + 1,
        supplier: supplier.supplier,
        totalSales: supplier.totalSales,
        percentage: parseFloat(percentage.toFixed(1)),
        growth: growth,
        color: colors[index] || "#6B7280",
        soldItems: supplier.soldItems,
        totalItems: supplier.totalItems,
        totalWeight: parseFloat(supplier.totalWeight.toFixed(2))
      };
    });
  }, [stock]);

  // Get top 5 suppliers for display
  const topSuppliers = supplierSalesData.slice(0, 5);
  const totalSales = supplierSalesData.reduce(
    (sum, supplier) => sum + supplier.totalSales,
    0
  );

  // Handle empty state
  if (!stock || stock.length === 0) {
    return (
      <Card
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundColor: isDark
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
          borderRadius: { xs: "16px", sm: "20px" },
          boxShadow: theme.shadows[4],
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <SupplierIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: isDark ? '#475569' : '#94a3b8', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            No Sales Data Available
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            No stock items found in inventory to calculate supplier sales.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (supplierSalesData.length === 0) {
    return (
      <Card
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundColor: isDark
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
          borderRadius: { xs: "16px", sm: "20px" },
          boxShadow: theme.shadows[4],
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <SupplierIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: isDark ? '#475569' : '#94a3b8', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            No Supplier Sales Data
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            No items have been sold yet. Supplier sales distribution will appear here once items are sold.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundColor: isDark
            ? "rgba(30, 41, 59, 0.5)"
            : "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
          borderRadius: { xs: "16px", sm: "20px" },
          boxShadow: theme.shadows[4],
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: theme.shadows[5],
          },
        }}
      >
        <CardContent
          sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Header */}
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              pb: { xs: 1.5, sm: 2.5 },
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              backgroundColor: isDark
                ? "rgba(30, 41, 59, 0.4)"
                : "rgba(255, 255, 255, 0.4)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  backgroundColor: alpha("#F59E0B", isDark ? 0.15 : 0.1),
                  borderRadius: "14px",
                  p: { xs: 1, sm: 1.5 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${alpha("#F59E0B", 0.2)}`,
                }}
              >
                <PieChartIcon sx={{ color: "#F59E0B", fontSize: { xs: 22, sm: 28 } }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", sm: "1.1rem" },
                    color: "text.primary",
                  }}
                >
                  Supplier Sales Distribution
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  }}
                >
                  <SupplierIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  Top {topSuppliers.length} Suppliers by Sales Value
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "#10B981", fontWeight: 700, fontSize: { xs: "0.8rem", sm: "0.95rem" } }}
            >
              Total: ₹{totalSales.toLocaleString()}
            </Typography>
          </Box>

          {/* Supplier List with Progress Bars */}
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {topSuppliers.map((supplier) => (
              <Box key={supplier.id} sx={{ mb: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      }}
                    >
                      {supplier.supplier}
                    </Typography>
                    {!isMobile && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {supplier.soldItems} sold • {supplier.totalItems} items • {supplier.totalWeight}gm
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? "#60A5FA" : "#2563EB",
                        fontFamily: "monospace",
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      }}
                    >
                      {supplier.percentage}%
                    </Typography>
                    <Chip
                      label={supplier.growth}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.6rem", sm: "0.7rem" },
                        backgroundColor: supplier.growth.startsWith("+")
                          ? alpha("#10B981", 0.1)
                          : alpha("#EF4444", 0.1),
                        color: supplier.growth.startsWith("+")
                          ? "#10B981"
                          : "#EF4444",
                        border: `1px solid ${supplier.growth.startsWith("+")
                          ? alpha("#10B981", 0.2)
                          : alpha("#EF4444", 0.2)
                          }`,
                        backdropFilter: "blur(10px)",
                        borderRadius: "6px",
                        height: { xs: 18, sm: 20 },
                      }}
                    />
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ position: "relative" }}>
                  <LinearProgress
                    variant="determinate"
                    value={supplier.percentage}
                    sx={{
                      height: { xs: 6, sm: 10 },
                      borderRadius: "5px",
                      backgroundColor: isDark
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.05)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: "5px",
                        background: `linear-gradient(90deg, ${supplier.color
                          } 0%, ${alpha(supplier.color, 0.7)} 100%)`,
                        boxShadow: `0 0 10px ${alpha(supplier.color, 0.3)}`,
                      },
                    }}
                  />
                </Box>

                {/* Additional Info - Show on mobile */}
                {isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 0.5,
                      px: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.65rem",
                      }}
                    >
                      {supplier.soldItems} sold
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.65rem",
                      }}
                    >
                      {supplier.totalWeight}gm
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#CBD5E1" : "#475569",
                        fontWeight: 500,
                        fontSize: "0.65rem",
                      }}
                    >
                      ₹{supplier.totalSales.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Footer with View All Button */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              pt: { xs: 1, sm: 1.5 },
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              backgroundColor: isDark
                ? "rgba(30, 41, 59, 0.4)"
                : "rgba(255, 255, 255, 0.4)",
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <TrendingUpIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
              Real-time data from {stock.length} inventory items • {supplierSalesData.length} suppliers total
            </Typography>

            {supplierSalesData.length > 5 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setViewAllModalOpen(true)}
                sx={{
                  borderRadius: "8px",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  fontWeight: 600,
                  textTransform: "none",
                  px: { xs: 1, sm: 2 },
                  py: 0.5,
                  borderColor: alpha("#6366F1", 0.5),
                  color: isDark ? "#A5B4FC" : "#4F46E5",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#6366F1",
                    backgroundColor: alpha("#6366F1", 0.08),
                    transform: "translateY(-1px)",
                  },
                }}
                startIcon={<ViewIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              >
                View All Suppliers
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* View All Suppliers Modal */}
      <Dialog
        open={viewAllModalOpen}
        onClose={() => setViewAllModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: '16px', sm: '20px' },
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
            background: isDark
              ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(30px)',
            border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
            boxShadow: isDark
              ? '0 20px 60px rgba(0, 0, 0, 0.4)'
              : '0 20px 60px rgba(0, 0, 0, 0.1)',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{
          p: { xs: 2, sm: 3 },
          pb: { xs: 1.5, sm: 2 },
          background: isDark
            ? 'linear-gradient(90deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)'
            : 'linear-gradient(90deg, rgba(139, 92, 246, 0.08) 0%, rgba(124, 58, 237, 0.04) 100%)',
          borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
            <Box sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: { xs: '10px', sm: '12px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isDark
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
              border: `1.5px solid ${alpha('#8b5cf6', isDark ? 0.3 : 0.2)}`,
            }}>
              <PieChartIcon sx={{ color: '#8b5cf6', fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{
                fontWeight: 700,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                color: isDark ? '#f1f5f9' : '#1e293b',
              }}>
                All Suppliers Sales Report
              </Typography>
              <Typography variant="body2" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                mt: 0.5,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
              }}>
                {supplierSalesData.length} suppliers • Total Sales: ₹{totalSales.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minHeight: '300px',
            overflowY: 'auto',
            pr: 1,
            pt: 2,
            pb: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: isDark ? alpha('#1e293b', 0.5) : alpha('#f1f5f9', 0.5),
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? '#475569' : '#cbd5e1',
              borderRadius: '10px',
              '&:hover': {
                background: isDark ? '#64748b' : '#94a3b8',
              }
            }
          }}>
            {supplierSalesData.map((supplier) => (
              <Card
                key={supplier.id}
                sx={{
                  borderRadius: '12px',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  border: `1.5px solid ${alpha(supplier.color, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(supplier.color, 0.4),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(supplier.color, 0.1)}`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                        <Box sx={{
                          width: { xs: 30, sm: 36 },
                          height: { xs: 30, sm: 36 },
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(supplier.color, 0.1),
                          border: `1.5px solid ${alpha(supplier.color, 0.3)}`,
                          color: supplier.color,
                          fontWeight: 700,
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        }}>
                          #{supplier.id}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '0.85rem', sm: '0.95rem' },
                              color: isDark ? '#e2e8f0' : '#1e293b',
                            }}
                          >
                            {supplier.supplier}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              flexWrap: 'wrap',
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={`${supplier.soldItems} sold`}
                                size="small"
                                sx={{
                                  backgroundColor: alpha('#10B981', 0.1),
                                  color: '#10B981',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                  height: { xs: 16, sm: 18 },
                                  borderRadius: '6px',
                                }}
                              />
                              <Chip
                                label={`${supplier.totalItems} items`}
                                size="small"
                                sx={{
                                  backgroundColor: alpha('#3B82F6', 0.1),
                                  color: '#3B82F6',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                  height: { xs: 16, sm: 18 },
                                  borderRadius: '6px',
                                }}
                              />
                              <Chip
                                label={`${supplier.totalWeight}gm`}
                                size="small"
                                sx={{
                                  backgroundColor: alpha('#F59E0B', 0.1),
                                  color: '#F59E0B',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                  height: { xs: 16, sm: 18 },
                                  borderRadius: '6px',
                                }}
                              />
                            </Box>
                          </Typography>
                        </Box>
                      </Box>

                      {/* Progress Bar */}
                      <Box sx={{ mt: 1.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                            Sales Percentage
                          </Typography>
                          <Typography variant="caption" sx={{
                            fontWeight: 700,
                            color: supplier.color,
                            fontFamily: 'monospace',
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          }}>
                            {supplier.percentage}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={supplier.percentage}
                          sx={{
                            height: { xs: 6, sm: 8 },
                            borderRadius: "4px",
                            backgroundColor: isDark
                              ? "rgba(255, 255, 255, 0.1)"
                              : "rgba(0, 0, 0, 0.05)",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: "4px",
                              background: `linear-gradient(90deg, ${supplier.color
                                } 0%, ${alpha(supplier.color, 0.7)} 100%)`,
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'right', minWidth: { xs: '100px', sm: '120px' } }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          color: isDark ? '#FBBF24' : '#D97706',
                          fontFamily: 'monospace',
                          mb: 0.5,
                        }}
                      >
                        ₹{supplier.totalSales.toLocaleString()}
                      </Typography>
                      <Chip
                        label={supplier.growth}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                          backgroundColor: supplier.growth.startsWith("+")
                            ? alpha("#10B981", 0.1)
                            : alpha("#EF4444", 0.1),
                          color: supplier.growth.startsWith("+")
                            ? "#10B981"
                            : "#EF4444",
                          border: `1px solid ${supplier.growth.startsWith("+")
                            ? alpha("#10B981", 0.2)
                            : alpha("#EF4444", 0.2)
                            }`,
                          backdropFilter: 'blur(10px)',
                          borderRadius: '6px',
                          height: { xs: 18, sm: 20 },
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          borderTop: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
        }}>
          <Button
            onClick={() => setViewAllModalOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              px: { xs: 2, sm: 4 },
              py: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.5),
              color: isDark ? '#cbd5e1' : '#475569',
              '&:hover': {
                borderColor: isDark ? '#64748b' : '#94a3b8',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// UPDATED: Improved Glassmorphism Table Component with real data
// Update the GlassTable component to accept footer action
const GlassTable = ({ title, columns, data, renderRows, totalInfo, footerAction }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        backgroundColor: isDark
          ? "rgba(30, 41, 59, 0.4)"
          : "rgba(255, 255, 255, 0.4)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
        borderRadius: { xs: "16px", sm: "20px" },
        boxShadow: theme.shadows[4],
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: theme.shadows[5],
        },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            pb: { xs: 1.5, sm: 2.5 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            backgroundColor: isDark
              ? "rgba(30, 41, 59, 0.4)"
              : "rgba(255, 255, 255, 0.4)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.9rem", sm: "1.1rem" },
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          {totalInfo && (
            <Typography
              variant="body2"
              sx={{ color: "#10B981", fontWeight: 700, fontSize: { xs: "0.75rem", sm: "0.95rem" } }}
            >
              {totalInfo}
            </Typography>
          )}
        </Box>

        {/* Improved Table - Scrollable on mobile */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <MuiTable sx={{ backgroundColor: "transparent", minWidth: isMobile ? 600 : 'auto' }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.4)"
                    : "rgba(255, 255, 255, 0.4)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {columns.map((col, idx) => (
                  <TableCell
                    key={idx}
                    align={col.align || "left"}
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.7rem", sm: "0.85rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      color: "text.secondary",
                      py: { xs: 2, sm: 2.5 },
                      px: { xs: 1.5, sm: 3 },
                      borderBottom: `1px solid ${alpha(
                        theme.palette.divider,
                        0.5
                      )}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderRows()}</TableBody>
          </MuiTable>
        </TableContainer>

        {/* NEW: Footer with View All button */}
        {footerAction && data.length > 0 && (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              pt: { xs: 1, sm: 1.5 },
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              backgroundColor: isDark
                ? "rgba(30, 41, 59, 0.4)"
                : "rgba(255, 255, 255, 0.4)",
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={footerAction.onClick}
              sx={{
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                textTransform: "none",
                px: { xs: 2, sm: 3 },
                py: { xs: 0.6, sm: 0.8 },
                borderColor: alpha("#6366F1", 0.5),
                color: isDark ? "#A5B4FC" : "#4F46E5",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: "#6366F1",
                  backgroundColor: alpha("#6366F1", 0.08),
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                },
              }}
              startIcon={footerAction.icon}
            >
              {footerAction.label || "View All"}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Glassmorphism Action Buttons
// Update the GlassActionButtons component to include navigation
const GlassActionButtons = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const actionButtons = [
    {
      label: "Add Customer",
      color: isDark ? "#60A5FA" : "#2563EB",
      gradient: isDark
        ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
        : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
      onClick: () => navigate('/customers'),
    },
    {
      label: "Add Supplier",
      color: isDark ? "#A78BFA" : "#8B5CF6",
      gradient: isDark
        ? "linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)"
        : "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
      onClick: () => navigate('/supplier'),
    },
    {
      label: "Add Item",
      color: isDark ? "#60A5FA" : "#2563EB",
      gradient: isDark
        ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
        : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
      onClick: () => navigate('/addItem'),
    },
    {
      label: "Create Bill",
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      onClick: () => navigate('/create-customer-transactions'),
    },
  ];

  // For mobile, show 2x2 grid
  return (
    <Card
      sx={{
        backgroundColor: isDark
          ? "rgba(30, 41, 59, 0.4)"
          : "rgba(255, 255, 255, 0.4)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
        borderRadius: { xs: "16px", sm: "20px" },
        boxShadow: theme.shadows[4],
        maxWidth: "750px",
        mt: 5,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2.5,
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            color: "text.primary",
          }}
        >
          Quick Actions
        </Typography>
        <Grid container spacing={1.5}>
          {actionButtons.map((button, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                fullWidth
                onClick={button.onClick}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.95rem" },
                  py: { xs: 1, sm: 1.5 },
                  px: { xs: 1, sm: 3 },
                  borderRadius: "12px",
                  textTransform: "none",
                  background: button.gradient,
                  boxShadow: isDark
                    ? "0 4px 14px 0 rgba(96, 165, 250, 0.35)"
                    : "0 4px 14px 0 rgba(37, 99, 235, 0.4)",
                  color: "#ffffff",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: button.gradient,
                    transform: "translateY(-2px)",
                    boxShadow: isDark
                      ? "0 6px 20px 0 rgba(96, 165, 250, 0.45)"
                      : "0 6px 20px 0 rgba(37, 99, 235, 0.5)",
                  },
                }}
              >
                {button.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

// FIXED: Calculate customer ledger with correct calculations
const calculateCustomerLedger = (customers, customerTransactions) => {
  if (!customers || !customerTransactions) {
    return [];
  }

  const customerLedgerData = customers
    .map(customer => {
      // Match transactions using multiple possible field names
      const customerTxns = customerTransactions.filter(t =>
        t.customer === customer.id ||
        t.customerId === customer.id ||
        t.customerName === customer.name
      ) || [];

      let totalSupplied = 0;
      let totalReceived = 0;

      customerTxns.forEach((transaction) => {
        // Calculate total supplied (grand total from summary)
        const grandTotal = parseFloat(transaction.summary?.grandTotal) || 0;
        totalSupplied += grandTotal;

        // Calculate total received - check multiple possible sources
        // Method 1: Direct from summary.paymentDone (from component logic)
        const paymentDone = parseFloat(transaction.summary?.paymentDone) || 0;

        // Method 2: Sum from paymentDetails (from calculateCustomerLedger logic)
        const cashPayment = parseFloat(transaction.paymentDetails?.cash) || 0;
        const bankPayment = parseFloat(transaction.paymentDetails?.bank) || 0;
        const paidAmount = parseFloat(transaction.paymentDetails?.paidAmount) || 0;
        const receivedAmount = parseFloat(transaction.paymentDetails?.received) || 0;
        const onlinePayment = parseFloat(transaction.paymentDetails?.online) || 0;
        const chequePayment = parseFloat(transaction.paymentDetails?.cheque) || 0;
        const otherPayment = parseFloat(transaction.paymentDetails?.other) || 0;

        // Use whichever is larger or sum them
        const paymentsFromDetails = cashPayment + bankPayment + paidAmount +
          receivedAmount + onlinePayment + chequePayment + otherPayment;

        // Take the maximum of the two methods
        totalReceived += Math.max(paymentDone, paymentsFromDetails);
      });

      // Opening balance from customer data
      const openingBalance = parseFloat(customer.openingBalance) ||
        parseFloat(customer.openingBalanceAmount) ||
        parseFloat(customer.initialBalance) ||
        0;

      // Calculate total pending (opening + supplied - received)
      const totalPending = openingBalance + totalSupplied - totalReceived;

      // Calculate advance if totalReceived > (openingBalance + totalSupplied)
      const netAdvance = totalReceived > (openingBalance + totalSupplied)
        ? totalReceived - (openingBalance + totalSupplied)
        : 0;

      return {
        id: customer.id,
        customer: customer.name || customer.customerName || 'Unknown Customer',
        openingBalance: openingBalance,
        totalSupplied: totalSupplied,
        totalReceived: totalReceived,
        totalPending: totalPending, // Show negative values for advance
        netAdvance: netAdvance,
        phone: customer.contact || customer.phone || customer.phoneNumber || "N/A",
        email: customer.emailId || customer.email || "N/A",
        transactionCount: customerTxns.length,
        // Additional fields to match component
        totalDue: totalSupplied,
        totalPaid: totalReceived,
        balance: totalPending,
        // Add fields needed for filtering
        recentTransactions: customerTxns.slice(0, 5).map(t => ({
          id: t.id,
          date: t.date,
          billNo: t.transactionDetails?.billNo || 'N/A',
          amount: t.summary?.grandTotal || 0,
          paid: t.summary?.paymentDone || 0,
          pending: t.summary?.pendingAmount || 0
        }))
      };
    })
    .filter(customer => {
      return customer.totalPending !== 0 || customer.transactionCount > 0 || customer.openingBalance !== 0;
    })
    .sort((a, b) => {
      // Sort by absolute pending amount (high to low), then by name
      if (Math.abs(a.totalPending) !== Math.abs(b.totalPending)) {
        return Math.abs(b.totalPending) - Math.abs(a.totalPending);
      }
      return a.customer.localeCompare(b.customer);
    })
    .slice(0, 5); // Get only first 5 items

  return customerLedgerData;
};

// UPDATED: Calculate real-time data for tables with safe date handling
const calculateRealTimeData = (customers, customerTransactions) => {
  console.log("Calculating real-time table data...");

  // Create a copy of transactions
  const allTransactions = customerTransactions ? [...customerTransactions] : [];

  // Sort transactions - LATEST FIRST (same as TransactionHistory)
  const sortedTransactions = allTransactions.sort((a, b) => {
    // Priority 1: Use createdAt timestamp if available (most accurate)
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt) - new Date(a.createdAt); // Newest first
    }

    // Priority 2: Use date + time if available
    if (a.transactionTime && b.transactionTime) {
      try {
        const parseDateTime = (transaction) => {
          if (!transaction.date || !transaction.transactionTime) return new Date(0);

          // Parse date in dd/MM/yyyy format
          const dateParts = transaction.date.split('/');
          if (dateParts.length !== 3) return new Date(0);

          const day = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1;
          const year = parseInt(dateParts[2], 10);

          // Parse time (assuming HH:MM format)
          const timeParts = transaction.transactionTime.split(':');
          const hours = parseInt(timeParts[0] || 0, 10);
          const minutes = parseInt(timeParts[1] || 0, 10);

          return new Date(year, month, day, hours, minutes);
        };

        const dateTimeA = parseDateTime(a);
        const dateTimeB = parseDateTime(b);
        return dateTimeB - dateTimeA; // Newest first
      } catch (error) {
        console.warn("Error parsing date with time:", error);
      }
    }

    // Priority 3: Fallback to just date (dd/MM/yyyy format) - LATEST FIRST
    try {
      const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0);

        // Handle dd/MM/yyyy format
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);

          // Validate
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }

        // Try ISO format
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) {
          return isoDate;
        }

        return new Date(0);
      };

      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA; // Newest first (changed from b - a to make it explicit)
    } catch (error) {
      console.warn("Error comparing dates:", error);
      return 0;
    }
  });

  console.log(`Sorted ${sortedTransactions.length} transactions, showing latest 5:`);
  console.log("Latest transactions:", sortedTransactions.slice(0, 3).map(t => ({
    date: t.date,
    billNo: t.transactionDetails?.billNo,
    createdAt: t.createdAt
  })));

  // Recent Transactions (last 5 transactions - which are the LATEST due to sorting)
  const recentTransactions = sortedTransactions.slice(0, 5).map((transaction, index) => {
    const customer = customers?.find(c => {
      return c.id === transaction.customer ||
        c.id === transaction.customerId ||
        c.name === transaction.customerName;
    });

    const amount = parseFloat(transaction.summary?.grandTotal) || 0;
    const pendingAmount = parseFloat(transaction.summary?.pendingAmount) || 0;
    const billNo = transaction.transactionDetails?.billNo || 'N/A';
    const itemsCount = transaction.items?.length || 0;

    // Determine status based on pending amount
    const status = pendingAmount === 0 ? "Completed" : "Pending";

    // Format date properly - show time if available
    let formattedDate = "N/A";
    if (transaction.date) {
      try {
        // Try to parse dd/MM/yyyy format
        const parts = transaction.date.split('/');
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];

          // Add time if available
          if (transaction.transactionTime) {
            const time = transaction.transactionTime.substring(0, 5); // Get HH:MM
            formattedDate = `${day}/${month}/${year} ${time}`;
          } else {
            formattedDate = `${day}/${month}/${year}`;
          }
        } else {
          formattedDate = transaction.date;
          if (transaction.transactionTime) {
            formattedDate += ` ${transaction.transactionTime.substring(0, 5)}`;
          }
        }
      } catch (error) {
        console.warn("Error formatting date:", error);
        formattedDate = transaction.date || "N/A";
      }
    }

    return {
      id: transaction.id || `trans_${index + 1}`,
      customer: customer?.name || `Customer ${transaction.customer || "Unknown"}`,
      amount: amount,
      date: formattedDate,
      status: status,
      billNo: billNo,
      itemsCount: itemsCount,
      // Additional fields
      customerId: transaction.customer,
      pendingAmount: pendingAmount,
      isPaid: pendingAmount === 0,
      // For debugging/verification
      originalDate: transaction.date,
      transactionTime: transaction.transactionTime,
      createdAt: transaction.createdAt
    };
  });

  // Customer Ledger - Top 3 pending amount customers
  const customerLedger = calculateCustomerLedger(customers, customerTransactions);

  return { recentTransactions, customerLedger };
};

export default function HomePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const {
    customers = [],
    stock = [],
    customerTransactions = [],
    loading,
  } = useContext(DataContext);


  // Calculate real dashboard stats
  const dashboardStats = useMemo(() =>
    calculateDashboardStats(customers, stock, customerTransactions),
    [customers, stock, customerTransactions]
  );

  // Calculate real-time table data
  const { recentTransactions, customerLedger } = useMemo(() =>
    calculateRealTimeData(customers, customerTransactions),
    [customers, customerTransactions]
  );

  const getStatusColor = (status) => {
    const colors = {
      Completed: { bg: "#10B981", text: "white" },
      Active: { bg: isDark ? "#60A5FA" : "#2563EB", text: "white" },
      Pending: { bg: "#F59E0B", text: "white" },
      Released: { bg: "#64748B", text: "white" },
    };
    return colors[status] || { bg: "#64748B", text: "white" };
  };

  const transactionColumns = [
    { label: "Customer", align: "left" },
    { label: "Bill No", align: "center" },
    { label: "Amount", align: "center" },
    { label: "Items", align: "center" },
    { label: "Date", align: "center" },
    { label: "Status", align: "center" },
  ];

  // Improved Table Row Component
  const TableRowStyled = ({ children, hover = true }) => (
    <TableRow
      sx={{
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        backgroundColor: isDark
          ? "rgba(30, 41, 59, 0.5)"
          : "rgba(255, 255, 255, 0.75)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": hover
          ? {
            backgroundColor: isDark
              ? "rgba(96, 165, 250, 0.1)"
              : "rgba(37, 99, 235, 0.08)",
          }
          : {},
        "&:last-child": {
          borderBottom: 0,
        },
      }}
    >
      {children}
    </TableRow>
  );

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          m: { xs: 1, sm: 2 },
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2, md: 3 },
          borderRadius: 2,
          background: isDark
            ? "linear-gradient(135deg, rgba(15, 23, 42, 0.3) 0%, rgba(30, 41, 59, 0.4) 100%)"
            : "linear-gradient(135deg, rgba(245, 247, 250, 0.3) 0%, rgba(228, 232, 240, 0.4) 100%)",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
          {/* Header */}
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 0.5,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              }}
            >
              Dashboard
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: { xs: "0.9rem", sm: "1.1rem" } }}>
              Welcome back! Here's your real-time business overview
            </Typography>
          </Box>


          {/* KPI + Actions + Events Row */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
            {/* LEFT: Stats + Quick Actions */}
            <Grid item xs={12} lg={9}>
              {/* KPI Stats with real data */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {dashboardStats.map((stat, idx) => (
                  <Grid item xs={6} sm={6} md={3} key={idx}>
                    <GlassStatCard {...stat} />
                  </Grid>
                ))}
              </Grid>

              {/* Quick Actions */}
              <GlassActionButtons />
            </Grid>

            {/* RIGHT: Birthday & Anniversary card */}
            <Grid item xs={12} lg={3}>
              <EventsReminderCard
                customers={customers}
                isDark={isDark}
              />
            </Grid>
          </Grid>

          {/* Supplier Sales Percentage Section */}
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <Grid sx={{ mb: 4 }}>
              <SupplierSalesCard stock={stock} />
            </Grid>
          </Box>

          {/* Recent Transactions - Glass Table */}
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <GlassTable
              title="Recent Transactions"
              columns={transactionColumns}
              data={recentTransactions}
              renderRows={() =>
                recentTransactions.map((row) => {
                  const statusColor = getStatusColor(row.status);
                  return (
                    <TableRowStyled key={row.id}>
                      <TableCell
                        sx={{
                          color: "text.primary",
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1.5, sm: 3 },
                          fontWeight: 600,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {row.customer}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: isDark ? "#8B5CF6" : "#7C3AED",
                          fontWeight: 700,
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1, sm: 3 },
                          fontFamily: "monospace",
                          fontSize: { xs: "0.7rem", sm: "0.9rem" },
                        }}
                      >
                        {row.billNo}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: isDark ? "#60A5FA" : "#2563EB",
                          fontWeight: 700,
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1.5, sm: 3 },
                          fontFamily: "monospace",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        ₹{row.amount.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "text.primary",
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1, sm: 3 },
                          fontWeight: 600,
                        }}
                      >
                        <Chip
                          label={`${row.itemsCount}`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.6rem", sm: "0.7rem" },
                            backgroundColor: isDark
                              ? "rgba(139, 92, 246, 0.2)"
                              : "rgba(139, 92, 246, 0.1)",
                            color: isDark ? "#A78BFA" : "#7C3AED",
                            backdropFilter: "blur(10px)",
                            borderRadius: "8px",
                            border: `1px solid ${isDark ? "rgba(167, 139, 250, 0.3)" : "rgba(139, 92, 246, 0.2)"}`,
                            minWidth: { xs: 30, sm: 50 },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "text.primary",
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1.5, sm: 3 },
                          fontFamily: "monospace",
                          fontSize: { xs: "0.7rem", sm: "0.9rem" },
                        }}
                      >
                        {row.date}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1.5, sm: 3 }
                        }}
                      >
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color: statusColor.text,
                            backgroundColor: statusColor.bg,
                            backdropFilter: "blur(10px)",
                            borderRadius: "8px",
                            minWidth: { xs: 60, sm: 90 },
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          }}
                        />
                      </TableCell>
                    </TableRowStyled>
                  );
                })
              }
              footerAction={{
                label: "View All Transactions",
                icon: <ViewIcon fontSize="small" />,
                onClick: () => navigate('/customer-transactions')
              }}
            />
          </Box>

          {/* Customer Ledger - Glass Table */}
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <GlassTable
              title="Customer Ledger (Top 5 Balance)"
              columns={[
                { label: "Customer", align: "left" },
                { label: "Opening", align: "right" },
                { label: "Supplied", align: "right" },
                { label: "Received", align: "right" },
                { label: "Balance", align: "right" },
                ...(isMobile ? [] : [{ label: "Contact", align: "left" }]),
              ]}
              data={customerLedger}
              totalInfo={`Total Balance: ₹${customerLedger
                .reduce((sum, customer) => sum + customer.totalPending, 0)
                .toLocaleString()}`}
              renderRows={() =>
                customerLedger.map((customer) => {
                  const hasBalance = customer.totalPending > 0;

                  return (
                    <TableRowStyled key={customer.id}>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1.5, sm: 3 },
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                            {customer.customer}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>
                            {customer.transactionCount} transactions
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#3B82F6",
                          fontWeight: 600,
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1, sm: 3 },
                          fontFamily: "monospace",
                          fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        }}
                      >
                        ₹{customer.openingBalance.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#EF4444",
                          fontWeight: 600,
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1, sm: 3 },
                          fontFamily: "monospace",
                          fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        }}
                      >
                        ₹{customer.totalSupplied.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#10B981",
                          fontWeight: 600,
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1, sm: 3 },
                          fontFamily: "monospace",
                          fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        }}
                      >
                        ₹{customer.totalReceived.toLocaleString()}
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          color: hasBalance ? "#F59E0B" : "#10B981",
                          fontWeight: 800,
                          py: { xs: 2, sm: 2.5 },
                          px: { xs: 1, sm: 3 },
                          fontFamily: "monospace",
                          backgroundColor: hasBalance
                            ? alpha("#F59E0B", 0.1)
                            : alpha("#10B981", 0.1),
                          borderRadius: "8px",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {hasBalance ? `₹${customer.totalPending.toLocaleString()}` : "₹0"}
                      </TableCell>

                      {!isMobile && (
                        <TableCell
                          sx={{
                            color: "text.primary",
                            py: { xs: 2, sm: 2.5 },
                            px: { xs: 1.5, sm: 3 },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                              📞 {customer.phone}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>
                              ✉️ {customer.email}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                    </TableRowStyled>
                  );
                })
              }
              footerAction={{
                label: "View All Customers",
                icon: <PeopleIcon fontSize="small" />,
                onClick: () => navigate('/customer-ledger')
              }}
            />
          </Box>

        </Container>
      </Box >
    </LocalizationProvider >
  );
}